import React, { useState } from 'react';
import { ArrowLeft, Sparkles, Users, BarChart3, MessageSquare, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ProfileGallery, PersonalityInsights, InterestsMatrix, ProfileCompletionTracker } from './EnhancedProfileSystem';
import { SmartMatchCard, AdvancedFilters, MatchSuggestionsFeed } from './SmartMatchingSystem';
import { PersonalAnalyticsDashboard, MatchSuccessAnalytics } from './AnalyticsDashboard';
import { EnhancedChatInterface, MatchPreviewCard, VideoCallPreview } from './EnhancedCommunication';

// Temporary local EnhancedButton to fix runtime error
const EnhancedButton = ({ children, variant = 'primary', size = 'md', icon, onClick, className = '', ...props }) => {
  const getVariantClasses = () => {
    switch (variant) {
      case 'primary':
        return 'bg-gradient-to-r from-rose-500 via-pink-500 to-rose-600 hover:from-rose-600 hover:via-pink-600 hover:to-rose-700 text-white';
      case 'outline':
        return 'border-2 border-rose-400 text-rose-400 hover:bg-rose-400 hover:text-white bg-transparent';
      case 'ghost':
        return 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 bg-transparent';
      default:
        return 'bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white';
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm': return 'px-4 py-2 text-sm';
      case 'lg': return 'px-8 py-4 text-lg';
      default: return 'px-6 py-3 text-base';
    }
  };

  return (
    <Button
      onClick={onClick}
      className={`${getVariantClasses()} ${getSizeClasses()} font-semibold rounded-xl transition-all duration-300 ${className}`}
      {...props}
    >
      <div className="flex items-center justify-center space-x-2">
        {icon && icon}
        {children && <span>{children}</span>}
      </div>
    </Button>
  );
};

const AdvancedFeaturesDemo = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profiles');
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [viewingProfile, setViewingProfile] = useState(null);

  // Complete matches data with different conversation contexts
  const matches = [
    { 
      id: 1, 
      name: "Sarah Chen", 
      photo: "https://images.unsplash.com/photo-1569913486515-b74bf7751574?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxhc2lhbiUyMHdvbWFuJTIwaGVhZHNob3R8ZW58MHx8fHwxNzU0MTMwODM1fDA&ixlib=rb-4.1.0&q=85", 
      isOnline: true, 
      compatibility: 94, 
      lastSeen: "Active now",
      lastMessage: "Looking forward to our hike!",
      bio: "Adventure seeker who loves hiking and photography"
    },
    { 
      id: 2, 
      name: "Emma Wilson", 
      photo: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxmZW1hbGUlMjBoZWFkc2hvdHxlbnwwfHx8fDE3NTQxMjk4MjJ8MA&ixlib=rb-4.1.0&q=85", 
      isOnline: false, 
      compatibility: 89, 
      lastSeen: "1h ago",
      lastMessage: "Start a conversation...",
      bio: "Coffee enthusiast and book lover"
    },
    { 
      id: 3, 
      name: "Zoe Martinez", 
      photo: "https://images.unsplash.com/photo-1581065178047-8ee15951ede6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NTQxMzA4NDR8MA&ixlib=rb-4.1.0&q=85", 
      isOnline: true, 
      compatibility: 82, 
      lastSeen: "Active now",
      lastMessage: "Start a conversation...",
      bio: "Fitness enthusiast and travel lover"
    }
  ];

  // Set default selected match when communication tab is active
  const currentMatch = selectedMatch || matches[0];

  const tabs = [
    { id: 'profiles', label: 'Enhanced Profiles', icon: <Users className="w-5 h-5" /> },
    { id: 'matching', label: 'Smart Matching', icon: <Sparkles className="w-5 h-5" /> },
    { id: 'analytics', label: 'Analytics', icon: <BarChart3 className="w-5 h-5" /> },
    { id: 'communication', label: 'Communication', icon: <MessageSquare className="w-5 h-5" /> }
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'profiles':
        return (
          <div className="space-y-8">
            <div className="text-center mb-8 animate-fadeInUp">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Enhanced Profile System</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Advanced profile features with photo galleries, personality insights, and detailed interests matrix
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <ProfileGallery canEdit={false} />
                <ProfileCompletionTracker />
              </div>
              
              <div className="space-y-6">
                <PersonalityInsights />
                <InterestsMatrix canEdit={false} />
              </div>
            </div>
          </div>
        );

      case 'matching':
        return (
          <div className="space-y-8">
            <div className="text-center mb-8 animate-fadeInUp">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Smart Matching System</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                AI-powered matching with swipeable cards, advanced filters, and intelligent suggestions
              </p>
            </div>

            <div className="flex flex-col lg:grid lg:grid-cols-3 gap-6 lg:gap-8">
              {/* Mobile: Show main card first, then suggestions */}
              <div className="lg:col-span-2 flex justify-center lg:order-2">
                {viewingProfile ? (
                  // Profile Details View
                  <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-gray-800">Profile Details</h3>
                      <button
                        onClick={() => setViewingProfile(null)}
                        className="text-gray-500 hover:text-gray-700 text-xl"
                      >
                        ×
                      </button>
                    </div>
                    
                    <div className="text-center mb-6">
                      <img
                        src={viewingProfile.photo}
                        alt={viewingProfile.name}
                        className="w-32 h-32 rounded-full mx-auto mb-4 object-cover"
                      />
                      <h2 className="text-2xl font-bold text-gray-800">
                        {viewingProfile.name}, {viewingProfile.age}
                      </h2>
                      <div className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium mt-2">
                        {viewingProfile.compatibility}% Match
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-gray-700 mb-2">Bio</h4>
                        <p className="text-gray-600 text-sm">
                          {viewingProfile.bio || "Adventure seeker who loves exploring new places and meeting interesting people. Looking for someone to share life's amazing moments with!"}
                        </p>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-gray-700 mb-2">Interests</h4>
                        <div className="flex flex-wrap gap-2">
                          {['Photography', 'Travel', 'Coffee', 'Hiking', 'Music'].map((interest, idx) => (
                            <span key={idx} className="bg-rose-100 text-rose-700 px-2 py-1 rounded-full text-xs">
                              {interest}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div className="pt-4 border-t">
                        <button
                          onClick={() => {
                            console.log('Started conversation with:', viewingProfile.name);
                            setViewingProfile(null);
                          }}
                          className="w-full bg-rose-500 text-white py-2 px-4 rounded-lg hover:bg-rose-600 transition-colors"
                        >
                          Start Conversation
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Smart Match Card
                  <SmartMatchCard 
                    onLike={(user) => {
                      console.log('Liked:', user.name);
                      // Could add notification or animation here
                    }}
                    onPass={(user) => {
                      console.log('Passed:', user.name);
                      // Could show next match
                    }}
                    onSuperLike={(user) => {
                      console.log('Super liked:', user.name);
                      // Could add special effect
                    }}
                  />
                )}
              </div>
              
              <div className="lg:col-span-1 space-y-6 lg:order-1">
                <AdvancedFilters />
                <MatchSuggestionsFeed 
                  onViewProfile={(user) => {
                    // Show profile information inline instead of navigation
                    setViewingProfile(user);
                    console.log('View profile clicked for:', user.name);
                  }}
                />
              </div>
            </div>
          </div>
        );

      case 'analytics':
        return (
          <div className="space-y-8">
            <div className="text-center mb-8 animate-fadeInUp">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Personal Analytics Dashboard</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Comprehensive insights into your dating performance with actionable recommendations
              </p>
            </div>

            <PersonalAnalyticsDashboard />
          </div>
        );

      case 'communication':
        return (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Enhanced Communication Tools</h2>
              <p className="text-gray-600">Advanced messaging with smart suggestions and real-time features</p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden animate-fadeInUp">
                <div className="p-4 bg-gradient-to-r from-rose-500 to-pink-500 text-white">
                  <h3 className="font-semibold">Your Matches</h3>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {matches.map((match, index) => (
                    <MatchPreviewCard 
                      key={match.id} 
                      match={match} 
                      onClick={() => setSelectedMatch(match)}
                      isSelected={currentMatch.id === match.id}
                      unreadCount={match.id === 1 ? 3 : 0}
                      lastMessage={match.lastMessage ? { text: match.lastMessage } : null}
                    />
                  ))}
                </div>
              </div>
              
              <div className="lg:col-span-3">
                <div className="h-96 lg:h-[500px]">
                  <EnhancedChatInterface match={currentMatch} />
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <EnhancedButton
                onClick={() => navigate('/')}
                variant="ghost"
                size="sm"
                icon={<ArrowLeft className="w-5 h-5" />}
              >
                Back to Home
              </EnhancedButton>
              
              <div className="h-6 w-px bg-gray-300"></div>
              
              <div>
                <h1 className="text-xl font-semibold text-gray-800">Advanced Features</h1>
                <p className="text-sm text-gray-600">Discover premium dating capabilities</p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Shot Clock Dating</span>
              <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center p-1">
                <img 
                  src="/shot-clock-icon-header.png" 
                  alt="Shot Clock Dating Icon"
                  className="w-6 h-6 object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation - Mobile Optimized */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto">
          <div className="flex overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex flex-col sm:flex-row items-center justify-center space-y-1 sm:space-y-0 sm:space-x-2 py-3 px-3 sm:px-4 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors min-w-0 flex-shrink-0 ${
                  activeTab === tab.id
                    ? 'border-rose-500 text-rose-600 bg-rose-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                }`}
                style={{ minWidth: '75px' }}
              >
                <div className="flex-shrink-0">
                  {tab.icon}
                </div>
                <span className="text-center leading-tight">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderContent()}
      </div>

      {/* Footer */}
      <div className="bg-gray-800 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center items-center space-x-2 mb-4">
            <div className="w-8 h-8 bg-gradient-to-r from-rose-500 to-pink-500 rounded-full flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-xl font-semibold">Shot Clock Dating</h3>
          </div>
          <p className="text-gray-400 mb-4">
            Experience the full power of AI-driven matching and premium features
          </p>
          <div className="flex justify-center space-x-6 text-sm text-gray-400">
            <span>✨ Enhanced Profiles</span>
            <span>🎯 Smart Matching</span>
            <span>📊 Analytics Dashboard</span>
            <span>💬 Advanced Chat</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedFeaturesDemo;